import * as APIRoutes from '@mm/api-routes';
import { PromiseHelpers } from '@mm/utils';
import WidgetModel, { WidgetTypes } from '../model/WidgetModel';
import AuthenticatedController from './AuthenticatedController';

// TS types are sorely lacking for widget.config so skipping types here
export const mapBEtoFE = item =>
  item.type === WidgetTypes.EMAIL
    ? ({
        ...item,
        config: {
          ...item.config,
          recipient: undefined,
          to: item.config.recipient,
        },
        validation: {
          ...item.validation,
        },
      } as any)
    : item;

// TS types are sorely lacking for widget.config so skipping types here
const mapFEtoBE = item =>
  item.type === WidgetTypes.EMAIL
    ? {
        ...item,
        config: {
          ...item.config,
          to: undefined,
          recipient: item.config.to,
        } as any,
      }
    : item;

export type SplitPageUsingGoToWidgetParams = {
  widgetId: number;
  pageId?: number;
  position: 'before' | 'after';
  branch: 'yes' | 'no';
  subtype?: 'unicorn_detector' | 'condition' | 'send_message';
};
export default class WidgetController extends AuthenticatedController {
  static async getAll(pageId: number): Promise<WidgetModel[]> {
    const response = await super.get(`${APIRoutes.PAGES}/${pageId}/widgets`);
    return response.map(mapBEtoFE);
  }

  static async getAllFromPages(pageIds: number[]) {
    const widgetPromisesMap = pageIds.reduce((widgetPromisesMap, pageId) => {
      widgetPromisesMap[pageId] = WidgetController.getAll(pageId);

      return widgetPromisesMap;
    }, {} as { [key: string]: Promise<WidgetModel[]> });

    return PromiseHelpers.allMap(widgetPromisesMap);
  }

  static async post(url, entity): Promise<WidgetModel> {
    const response = await super.post(url, entity);
    return mapBEtoFE(response);
  }

  static async getWidget(
    widgetId: number,
    includeStructure?: boolean,
  ): Promise<WidgetModel> {
    const response = await super.get(
      `${APIRoutes.WIDGETS}/${widgetId}${
        includeStructure ? '?include_structure=1' : ''
      }`,
    );
    return mapBEtoFE(response);
  }

  static async putWidget(entity): Promise<WidgetModel> {
    const response = await super.put(
      `${APIRoutes.WIDGETS}/${entity.id}`,
      mapFEtoBE(entity),
    );
    return mapBEtoFE(response);
  }

  static async patchWidget(
    dialogueId: number,
    entities: WidgetModel[],
    options: {
      sortOrdersOnly?: boolean;
      normalizeOrdering?: boolean;
      syncButtons?: boolean;
    },
  ): Promise<WidgetModel[]> {
    const {
      sortOrdersOnly = false,
      normalizeOrdering = false,
      syncButtons = false,
    } = options;
    const widgets = sortOrdersOnly
      ? entities.map(e => ({ sortOrder: e.sortOrder, id: e.id }))
      : entities;

    const response = await super.patch(`${APIRoutes.WIDGETS}/batch_update`, {
      widgets,
      sortOrdersOnly,
      pageId: dialogueId,
      normalizeOrdering,
      syncButtons,
    });

    return response.map(mapBEtoFE);
  }

  static deleteWidget(id: number): Promise<any> {
    return super.delete(`${APIRoutes.WIDGETS}/${id}`);
  }

  static deleteEntry(widgetId: number, id: number): Promise<any> {
    return super.delete(`${APIRoutes.WIDGETS}/${widgetId}/entries/${id}`);
  }

  static deleteButton(widgetId: number, id: number): Promise<any> {
    return super.delete(`${APIRoutes.WIDGETS}/${widgetId}/buttons/${id}`);
  }

  static splitPageUsingGoToWidget(params: SplitPageUsingGoToWidgetParams) {
    const { widgetId, ...otherParams } = params;

    return super.post(
      `${APIRoutes.WIDGETS}/split_page_using_go_to_widget/${widgetId}`,
      otherParams,
    );
  }

  static batchDelete(widgetIds: number[]): Promise<any> {
    return super.delete(`${APIRoutes.WIDGETS}/batch_destroy`, {
      widget_ids: widgetIds.join(','),
    });
  }
}
